import React from 'react';
import { useDashboardData } from './useDashboardData';
import { useLayout } from '../../layout/LayoutContext';
import { RefreshIcon } from '../../components/ui/Icons';
import { chartColors } from './chartTheme';
import KpiCards from './components/KpiCards';
import OtdCard from './components/OtdCard';
import PromoCard from './components/PromoCard';
import LoadsTrendCard from './components/LoadsTrendCard';
import ClientAnalysisCard from './components/ClientAnalysisCard';
import OperationTypeCard from './components/OperationTypeCard';
import BarDistributionCard from './components/BarDistributionCard';
import DashboardSkeleton from './components/DashboardSkeleton';
import WhatsNewButton from './components/WhatsNewButton';
import RecentLoadsCard from './components/RecentLoadsCard';
import LoadsByStatusCard from './components/LoadsByStatusCard';
import AttentionCard from './components/AttentionCard';
import CarriersCard from './components/CarriersCard';
import styles from './Dashboard.module.css';

const Dashboard: React.FC = () => {
  const { data, loading, error, refetch } = useDashboardData();
  const { setHeaderActions } = useLayout();

  // Contador que se incrementa al pulsar Refresh, para forzar la recarga del
  // OTD (que tiene su propio fetch por filtro, independiente del dashboard).
  const [refreshKey, setRefreshKey] = React.useState(0);
  const handleRefresh = React.useCallback(() => {
    refetch();
    setRefreshKey((k) => k + 1);
  }, [refetch]);

  // Inyecta los botones del header una sola vez (deps estables).
  // `handleRefresh` es estable (useCallback), así se evita reinyectar (y
  // re-renderizar el layout) en cada cambio de estado.
  // 🔄 20-oct-2026: "What's new" va a la izquierda de "Refresh", que se queda
  // en su lugar. Su indicador se actualiza dentro del propio botón
  // (`WhatsNewButton`), así que tampoco obliga a reinyectar.
  React.useEffect(() => {
    setHeaderActions(
      <>
        <WhatsNewButton className={styles.resetBtn} />
        <button type="button" className={styles.resetBtn} onClick={handleRefresh}>
          <RefreshIcon size={16} />
          Refresh
        </button>
      </>
    );
    return () => setHeaderActions(null);
  }, [setHeaderActions, handleRefresh]);

  if (loading && !data) {
    return <DashboardSkeleton />;
  }

  if (error && !data) {
    return (
      <div className={styles.state}>
        <p>Could not load the dashboard.</p>
        <button type="button" className={styles.resetBtn} onClick={refetch}>
          <RefreshIcon size={16} />
          Retry
        </button>
      </div>
    );
  }

  const metrics = data?.metrics;

  /**
   * 🔄 20-oct-2026: *Client Analysis* solo cuando trae datos (*"se ve muy vacio para los clientes"*).
   *
   * El API manda `topClientsByVolume` solo a quien tiene permisos de clientes; a un cliente no, y la tarjeta salía como
   * un cuadro gris vacío. Sin esos datos, en su lugar van sus cargas recientes, y en la columna derecha, en lugar de
   * *"Find every metric here"*, sus cargas por estatus: las dos cosas ya venían en la respuesta (`recentLoads`,
   * `loadsByStatus`). Para quien sí ve *Client Analysis* el tablero queda igual.
   *
   * 🔄 Esa misma tarde, *"falta algo de la columna de la derecha queda muy vacia"*: debajo del OTD van también lo que
   * va tarde (`delayedLoads`) y sus transportistas con su tránsito promedio (`topCarriers`, `transitTimeByCarrier`).
   * Tres cosas más que el API ya le mandaba al cliente y nadie mostraba.
   */
  const showsClientAnalysis = (metrics?.topClientsByVolume?.length ?? 0) > 0;

  const officeData =
    metrics?.loadsByOffice?.map((o) => ({
      name: o.office_name,
      value: o.total_loads,
    })) ?? [];

  const modeData =
    metrics?.loadsByMode?.map((m) => ({
      name: m.mode_name,
      value: m.total_loads,
    })) ?? [];

  return (
    <div className={`${styles.page} dylo-animate-in`}>
      <div className={styles.topGrid}>
        <div className={styles.mainCol}>
          <KpiCards metrics={metrics?.basicMetrics} />
          <LoadsTrendCard data={metrics?.loadsTrend} />
          {showsClientAnalysis ? (
            <ClientAnalysisCard data={metrics?.topClientsByVolume} />
          ) : (
            <RecentLoadsCard loads={metrics?.recentLoads} />
          )}
        </div>

        <div className={styles.sideCol}>
          {metrics?.onTimeDeliveryRate && (
            <OtdCard data={metrics.onTimeDeliveryRate} refreshKey={refreshKey} />
          )}
          {showsClientAnalysis ? (
            <PromoCard />
          ) : (
            <>
              <AttentionCard loads={metrics?.delayedLoads} />
              <LoadsByStatusCard data={metrics?.loadsByStatus} />
              <CarriersCard top={metrics?.topCarriers} transit={metrics?.transitTimeByCarrier} />
            </>
          )}
          {metrics?.loadsByOperationType &&
            metrics.loadsByOperationType.length > 0 && (
              <OperationTypeCard data={metrics.loadsByOperationType} />
            )}
        </div>
      </div>

      {(officeData.length > 0 || modeData.length > 0) && (
        <div className={styles.bottomRow}>
          {officeData.length > 0 && (
            <BarDistributionCard
              title="Distribution by Office"
              data={officeData}
              color={chartColors.office}
              footer="DYLO OFFICE'S"
            />
          )}
          {modeData.length > 0 && (
            <BarDistributionCard
              title="Distribution by Mode"
              data={modeData}
              color={chartColors.mode}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
